import { newEnforcer, Enforcer, StringAdapter } from 'casbin';
import { rbacPolicies, roleHierarchy } from './policies';
import modelConf from './model.conf?raw';

// Casbin Enforcer Service - Single Responsibility
export class CasbinEnforcer {
  private enforcer: Enforcer | null = null;

  async init(): Promise<void> {
    // Convert policies to CSV format
    const policyCSV = rbacPolicies.map((p) => `p, ${p.join(', ')}`).join('\n');
    const roleCSV = roleHierarchy.map((r) => `g, ${r.join(', ')}`).join('\n');
    const csvContent = `${policyCSV}\n${roleCSV}`;

    // Create adapter from string
    const adapter = new StringAdapter(csvContent);

    // Create enforcer
    this.enforcer = await newEnforcer(modelConf, adapter);
  }

  async enforce(sub: string, obj: string, act: string): Promise<boolean> {
    if (!this.enforcer) {
      await this.init();
    }
    return this.enforcer!.enforce(sub, obj, act);
  }

  async hasPermission(role: string, resource: string, action: string): Promise<boolean> {
    return this.enforce(role, resource, action);
  }

  async getRolesForUser(user: string): Promise<string[]> {
    if (!this.enforcer) {
      await this.init();
    }
    return this.enforcer!.getRolesForUser(user);
  }

  async getPermissionsForUser(user: string): Promise<string[][]> {
    if (!this.enforcer) {
      await this.init();
    }
    return this.enforcer!.getPermissionsForUser(user);
  }

  async addRoleForUser(user: string, role: string): Promise<boolean> {
    if (!this.enforcer) {
      await this.init();
    }
    return this.enforcer!.addRoleForUser(user, role);
  }

  async deleteRoleForUser(user: string, role: string): Promise<boolean> {
    if (!this.enforcer) {
      await this.init();
    }
    return this.enforcer!.deleteRoleForUser(user, role);
  }
}

// Singleton instance
let casbinEnforcer: CasbinEnforcer | null = null;

export async function getCasbinEnforcer(): Promise<CasbinEnforcer> {
  if (!casbinEnforcer) {
    casbinEnforcer = new CasbinEnforcer();
    await casbinEnforcer.init();
  }
  return casbinEnforcer;
}
